import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/client'
import dayjs from 'dayjs'
import { Button, Card, DatePicker, Input, Popconfirm, Select, Space, Table, Tag, Typography, message } from 'antd'
import { CheckOutlined, CloseOutlined, CopyOutlined, PlusOutlined } from '@ant-design/icons'

import Layout from '../App/layout'
import LayoutContentWrapper from '../../components/utility/layoutWrapper'
import ContentLayout from '../../components/utility/layoutContent'
import CurrentStaffContainer from '../CurrentStaff/CurrentStaffContainer'
import SearchStaff from '../SearchStaff'

import {
  CALENDAR_FEED_ROLES,
  DELETE_LEAVE_DAY_MUTATION,
  LEAVE_CALENDAR_URL_QUERY,
  LEAVE_DAYS_QUERY,
  LEAVE_STATUSES,
  MANAGER_ROLES,
  STATUS_COLORS,
  UPDATE_LEAVE_DAY_MUTATION,
  formatMutationErrors
} from './graphql'

const PAGE_SIZE = 20
const DATE_FORMAT = 'YYYY-MM-DD'
const DISPLAY_FORMAT = 'DD/MM/YYYY'

const formatDate = value => (value ? dayjs(value).format(DISPLAY_FORMAT) : '-')

const leaveLength = ({ startDate, endDate }) => {
  if (!startDate || !endDate) return null

  return dayjs(endDate).diff(dayjs(startDate), 'day') + 1
}

const CalendarFeed = ({ url }) => {
  const { t } = useTranslation('leaveDays')

  const handleCopy = () => {
    navigator.clipboard.writeText(url)
      .then(() => message.success(t('copied')))
      .catch(() => message.error(t('copyFailed')))
  }

  return (
    <Card size='small' title={t('calendarFeed')} style={{ marginBottom: 16 }}>
      <Typography.Paragraph type='secondary'>{t('calendarFeedHint')}</Typography.Paragraph>
      <Space.Compact style={{ width: '100%' }}>
        <Input readOnly value={url} />
        <Button icon={<CopyOutlined />} onClick={handleCopy}>{t('copyCalendarUrl')}</Button>
      </Space.Compact>
    </Card>
  )
}

CalendarFeed.propTypes = {
  url: PropTypes.string.isRequired
}

const LeaveDaysIndexContainer = ({ rolesMatched, currentStaffLoading }) => {
  const { t } = useTranslation('leaveDays')
  const isManager = rolesMatched(MANAGER_ROLES)
  const canSeeCalendarFeed = rolesMatched(CALENDAR_FEED_ROLES)

  const [page, setPage] = useState(1)
  const [staff, setStaff] = useState(undefined)
  const [status, setStatus] = useState(undefined)
  const [dateRange, setDateRange] = useState(null)
  const [busyId, setBusyId] = useState(null)

  useEffect(() => {
    document.title = 'Leave Days | OSH CRM'
  }, [])

  const variables = {
    page,
    per: PAGE_SIZE,
    staffId: (isManager && staff && staff.key) || undefined,
    status,
    startDate: dateRange ? dateRange[0].format(DATE_FORMAT) : undefined,
    endDate: dateRange ? dateRange[1].format(DATE_FORMAT) : undefined
  }

  const { data, loading, refetch } = useQuery(LEAVE_DAYS_QUERY, {
    variables,
    fetchPolicy: 'cache-and-network'
  })

  const { data: calendarData } = useQuery(LEAVE_CALENDAR_URL_QUERY, {
    skip: !canSeeCalendarFeed,
    errorPolicy: 'ignore'
  })

  const [updateLeaveDay] = useMutation(UPDATE_LEAVE_DAY_MUTATION)
  const [deleteLeaveDay] = useMutation(DELETE_LEAVE_DAY_MUTATION)

  const result = (data && data.leaveDays) || {}
  const calendarUrl = calendarData && calendarData.leaveCalendarUrl

  const resetPage = setter => value => {
    setter(value)
    setPage(1)
  }

  const handleSetStatus = async (leaveDay, nextStatus) => {
    setBusyId(leaveDay.id)
    try {
      const { data: response } = await updateLeaveDay({ variables: { id: leaveDay.id, status: nextStatus } })
      const payload = response && response.updateLeaveDay
      if (!payload) { message.error(t('saveFailed')); return }
      if (payload.errors && payload.errors.length) { message.error(formatMutationErrors(payload.errors)); return }

      message.success(t(nextStatus === 'approve' ? 'approveSuccess' : 'rejectSuccess'))
      refetch()
    } catch (error) {
      message.error(error.message)
    } finally {
      setBusyId(null)
    }
  }

  const handleDelete = async (leaveDay) => {
    setBusyId(leaveDay.id)
    try {
      const { data: response } = await deleteLeaveDay({ variables: { id: leaveDay.id } })
      const payload = response && response.deleteLeaveDay
      if (!payload) { message.error(t('deleteFailed')); return }
      if (payload.errors && payload.errors.length) { message.error(formatMutationErrors(payload.errors)); return }

      message.success(t('deleteSuccess'))
      refetch()
    } catch (error) {
      message.error(error.message)
    } finally {
      setBusyId(null)
    }
  }

  const columns = [
    {
      title: t('dates'),
      key: 'dates',
      render: (_value, record) => {
        const days = leaveLength(record)

        return (
          <span>
            {formatDate(record.startDate)} - {formatDate(record.endDate)}
            {days ? <Typography.Text type='secondary'> ({t('daysCount', { count: days })})</Typography.Text> : null}
          </span>
        )
      }
    },
    {
      title: t('leaveType'),
      dataIndex: 'leaveType',
      key: 'leaveType',
      render: value => (value ? t(`leaveTypes.${value}`) : '-')
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      render: value => (value ? <Tag color={STATUS_COLORS[value]}>{t(`statuses.${value}`)}</Tag> : '-')
    },
    isManager && {
      title: t('staff'),
      key: 'staff',
      render: (_value, record) => (record.staff && record.staff.fullName) || '-'
    },
    {
      title: t('description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true
    },
    {
      title: '',
      key: 'actions',
      align: 'right',
      render: (_value, record) => (
        <Space wrap>
          {record.canApprove && record.status === 'pending' && (
            <>
              <Button
                icon={<CheckOutlined />}
                loading={busyId === record.id}
                onClick={() => handleSetStatus(record, 'approve')}
                size='small'
                type='primary'
              >
                {t('approve')}
              </Button>
              <Button
                danger
                icon={<CloseOutlined />}
                loading={busyId === record.id}
                onClick={() => handleSetStatus(record, 'no_approve')}
                size='small'
              >
                {t('reject')}
              </Button>
            </>
          )}
          <Link to={`/leave_days/${record.id}/edit`}>
            {record.canUpdate ? t('edit') : t('view')}
          </Link>
          {record.canDelete && (
            <Popconfirm
              cancelText={t('cancel')}
              okText={t('delete')}
              onConfirm={() => handleDelete(record)}
              title={t('deleteConfirm')}
            >
              <Button danger loading={busyId === record.id} size='small' type='link'>{t('delete')}</Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ].filter(Boolean)

  return (
    <Layout>
      <LayoutContentWrapper breadcrumbs={[
        { path: '/leave_days', name: t('title') }
      ]}>
        <ContentLayout>
          {canSeeCalendarFeed && calendarUrl && <CalendarFeed url={calendarUrl} />}

          <Space wrap style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
            <Space wrap>
              {isManager && (
                <SearchStaff onChange={resetPage(setStaff)} value={staff} />
              )}
              <Select
                allowClear
                onChange={resetPage(setStatus)}
                placeholder={t('allStatuses')}
                style={{ width: 160 }}
                value={status}
              >
                {LEAVE_STATUSES.map(value => (
                  <Select.Option key={value} value={value}>{t(`statuses.${value}`)}</Select.Option>
                ))}
              </Select>
              <DatePicker.RangePicker
                format={DISPLAY_FORMAT}
                onChange={resetPage(setDateRange)}
                value={dateRange}
              />
            </Space>
            <Link to='/leave_days/new'>
              <Button icon={<PlusOutlined />} type='primary'>{t('newLeave')}</Button>
            </Link>
          </Space>

          <Table
            columns={columns}
            dataSource={result.leaveDays || []}
            loading={loading || currentStaffLoading}
            onChange={pagination => setPage(pagination.current)}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              showSizeChanger: false,
              total: result.totalCount || 0
            }}
            rowKey='id'
            scroll={{ x: true }}
          />
        </ContentLayout>
      </LayoutContentWrapper>
    </Layout>
  )
}

LeaveDaysIndexContainer.propTypes = {
  currentStaffLoading: PropTypes.bool,
  rolesMatched: PropTypes.func.isRequired
}

LeaveDaysIndexContainer.defaultProps = {
  currentStaffLoading: false
}

export default CurrentStaffContainer(LeaveDaysIndexContainer)
